import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { User } from 'graphql/generated/graphql'
import { AppDispatch } from './store'
import { clearOnboardingReminder } from '@/lib/onboarding'

const JwtToken: string | undefined = ''

const UserObj: Record<string, any> = {}

type FollowersObj = {
  MyFollowing: {
    results: User[]
  }
  MyFollowers: {
    results: User[]
  }
}


type PendingFollow = {
  targetId: string | number
  targetType: string
} | undefined

const initialState = {
  jwt_token: JwtToken,
  user: UserObj,
  // null (not an empty-but-truthy object) so consumers can tell "never fetched"
  // apart from "fetched, and it's genuinely empty".
  followerFollowing: null as FollowersObj | null,
  redirectUrl: undefined,
  shouldRedirect: false,
  referrer: undefined,
  defaultListingID: undefined,
  pendingFollow: undefined as PendingFollow,
  resetCode: undefined,
  // When true, suppresses AuthWrapper's generic "authenticated on a public
  // route -> /discover" redirect. Used by the plain-signup fallback modal
  // (app/auth/join-culineer/RedirectModal.tsx) to stay on the signup page
  // long enough to actually show, instead of losing the race to that
  // immediate, undelayed redirect.
  suppressAutoRedirect: false,
}


const authSlice = createSlice({
  name: 'culineerAuth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.jwt_token = action.payload?.token || state.jwt_token
      if (action.payload?.user) {
        state.user = action.payload?.user
      } else {
        state.user = { ...state.user, ...action.payload }
      }
    },
    setSwitchedRole: (state, action) => {
      // Both copies are read around the app (auth wrapper and layouts use userRoles, share modal uses roles),
      // so a switch must update both or one of them keeps pointing at the old default role.
      state.user.roles = action.payload
      state.user.userRoles = action.payload
    },

    setSwitchedListing: (state, action) => {
      state.user.default_listing_id = action.payload.default_listing_id
      state.user.default_listing = action.payload.default_listing
      state.defaultListingID = action.payload.default_listing_id
    },

    setUpdateUserListing: (state, action) => {
      const updatedListing = action.payload;
      
      // check if listing already exists
      const index = state.user.listings.findIndex(
        (listing) => listing.listing_id === updatedListing.listing_id
      );

      if (index !== -1) {
        // replace existing listing
        state.user.listings[index] = updatedListing;
      } else {
        // add new listing
        state.user.listings.push(updatedListing);
      }
    },

    setUserProfileImage: (state, action) => {
      state.user.photo_resource = action.payload
    },

    resetUserProfileImage: (state) => {
      state.user.photo_resource = null
    },

    setResetCode: (state, code) => {
      state.resetCode = code
    },

    removeResetCode: (state) => {
      state.resetCode = undefined
    },

    logOut: (state) => {
      state.jwt_token = ''
      state.user = {}
      state.followerFollowing = null
      state.redirectUrl = undefined
      state.shouldRedirect = false
      state.pendingFollow = undefined
      state.suppressAutoRedirect = false
    },
    setRedirectUrl: (state, action) => {
      state.redirectUrl = action.payload
    },
    setShouldRedirect: (state, action) => {
      state.shouldRedirect = action.payload
    },
    setSuppressAutoRedirect: (state, action: PayloadAction<boolean>) => {
      state.suppressAutoRedirect = action.payload
    },
    setPendingFollow: (state, action: PayloadAction<PendingFollow>) => {
      state.pendingFollow = action.payload
    },
    setReferrer: (state, action) => {
      state.referrer = action.payload
    },
    setFollowerFOllowing: (state, action: PayloadAction<FollowersObj>) => {
      state.followerFollowing = action.payload
    },
  },
})

export const {
  setUser,
  logOut,
  setSwitchedRole,
  setSwitchedListing,
  setRedirectUrl,
  setResetCode,
  removeResetCode,
  setFollowerFOllowing,
  setShouldRedirect,
  setPendingFollow,
  setSuppressAutoRedirect,
  setReferrer,
  setUserProfileImage,
  resetUserProfileImage,
  setUpdateUserListing
} = authSlice.actions


export const logOutAndClearReminder = () => (dispatch: AppDispatch) => {
  dispatch(logOut())
  clearOnboardingReminder()
}

export default authSlice.reducer
